import React, { useRef, useState } from 'react';

import { useTranslation } from '@modernloop/i18n';
import {
  LinkIcon,
  NoneIcon,
  PhoneWithArrowPointingInwardsIcon,
  PhoneWithArrowPointingOutwardsIcon,
  TextIcon,
  TriangleDownIcon,
} from '@modernloop/shared/icons';
import { GoogleMeetLogo, MicrosoftTeamsLogo, ZoomLogo } from '@modernloop/shared/images';
import {
  Avatar,
  Button,
  Divider,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';

import { DirectoryType, InterviewMeetingLocationType } from 'src/generated/mloop-graphql';

import useDirectoryInfo from 'src/hooks/useDirectoryInfo';
import { useHasZoomIntegration } from 'src/hooks/useHasZoomIntegration';

import { InterviewMeetingLocationVariant } from './types';
import { getInterviewMeetingLocationIcon } from './utils';

type Props = {
  disabled: boolean;
  variant?: InterviewMeetingLocationVariant;
  interviewMeetingLocationType: InterviewMeetingLocationType;
  onChange: (interviewMeetingLocationType: InterviewMeetingLocationType) => void;
};

const InterviewMeetingLocationMenu = ({
  disabled,
  variant = InterviewMeetingLocationVariant.INTERVIEW,
  interviewMeetingLocationType,
  onChange,
}: Props) => {
  const { t } = useTranslation();
  // A debrief has no candidate, so calling the candidate's phone is not an option (and the backend
  // validator rejects CANDIDATE_PHONE for debrief prefs).
  const showCandidatePhone = variant !== InterviewMeetingLocationVariant.DEBRIEF;
  const hasZoom = useHasZoomIntegration();
  const directoryInfo = useDirectoryInfo();
  const hasMicrosoft =
    directoryInfo.type === DirectoryType.Outlook ||
    directoryInfo.directoryIntegrations?.some((integration) => integration.type === DirectoryType.Outlook);
  const hasGoogle =
    directoryInfo.type === DirectoryType.Google ||
    directoryInfo.directoryIntegrations?.some((integration) => integration.type === DirectoryType.Google);

  const [open, setOpen] = useState(false);

  const locationMenuRef = useRef<HTMLButtonElement | null>(null);

  const handleMenuItemClick = (type: InterviewMeetingLocationType) => {
    onChange(type);
    setOpen(false);
  };

  const getLocationLabel = (locationType: InterviewMeetingLocationType) => {
    switch (locationType) {
      case InterviewMeetingLocationType.Zoom:
        return t('hiring-dashboard-common:common.location.zoom');
      case InterviewMeetingLocationType.GoogleMeet:
        return t('hiring-dashboard-common:common.location.googleMeet');
      case InterviewMeetingLocationType.MicrosoftTeams:
        return t('hiring-dashboard-common:common.location.microsoftTeams');
      case InterviewMeetingLocationType.CustomLink:
        return t('hiring-dashboard-common:common.location.customLink');
      case InterviewMeetingLocationType.CandidatePhone:
        return t('hiring-dashboard-common:common.location.candidatePhone');
      case InterviewMeetingLocationType.CustomPhone:
        return t('hiring-dashboard-common:common.location.customPhone');
      case InterviewMeetingLocationType.CustomText:
        return t('hiring-dashboard-common:common.location.customText');
      case InterviewMeetingLocationType.None:
        return t('hiring-dashboard-common:common.none');
      default:
        return t('hiring-dashboard-common:common.none');
    }
  };

  const renderLabel = () => {
    const icon = getInterviewMeetingLocationIcon(interviewMeetingLocationType);

    return (
      <Stack direction="row" spacing={1}>
        {icon}
        <Typography>{getLocationLabel(interviewMeetingLocationType)}</Typography>
      </Stack>
    );
  };

  return (
    <>
      <Button
        data-testid={`interview-meeting-location-menu-button-${interviewMeetingLocationType}`}
        fullWidth
        disabled={disabled}
        ref={locationMenuRef}
        endIcon={<TriangleDownIcon />}
        sx={{ borderRadius: '6px', justifyContent: 'space-between' }}
        onClick={() => setOpen(true)}
      >
        {renderLabel()}
      </Button>
      <Menu
        open={open}
        anchorEl={locationMenuRef.current}
        slotProps={{ paper: { sx: { width: '400px' } } }}
        onClose={() => setOpen(false)}
      >
        <ListSubheader>{t('hiring-dashboard-common:common.location.videoConferencing')}</ListSubheader>

        {hasZoom && (
          <MenuItem
            data-testid={`interview-meeting-location-menu-item-${InterviewMeetingLocationType.Zoom}`}
            value={InterviewMeetingLocationType.Zoom}
            onClick={() => handleMenuItemClick(InterviewMeetingLocationType.Zoom)}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <ListItemIcon>
                <Avatar src={ZoomLogo} />
              </ListItemIcon>
              <ListItemText>
                <Typography>{t('hiring-dashboard-common:common.location.zoom')}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ textWrap: 'wrap' }}>
                  {t('hiring-dashboard-common:common.location.zoomDesc')}
                </Typography>
              </ListItemText>
            </Stack>
          </MenuItem>
        )}

        {hasGoogle && (
          <MenuItem
            data-testid={`interview-meeting-location-menu-item-${InterviewMeetingLocationType.GoogleMeet}`}
            value={InterviewMeetingLocationType.GoogleMeet}
            onClick={() => handleMenuItemClick(InterviewMeetingLocationType.GoogleMeet)}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <ListItemIcon>
                <Avatar src={GoogleMeetLogo} />
              </ListItemIcon>
              <ListItemText>
                <Typography>{t('hiring-dashboard-common:common.location.googleMeet')}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ textWrap: 'wrap' }}>
                  {t('hiring-dashboard-common:common.location.googleMeetDesc')}
                </Typography>
              </ListItemText>
            </Stack>
          </MenuItem>
        )}

        {hasMicrosoft && (
          <MenuItem
            data-testid={`interview-meeting-location-menu-item-${InterviewMeetingLocationType.MicrosoftTeams}`}
            value={InterviewMeetingLocationType.MicrosoftTeams}
            onClick={() => handleMenuItemClick(InterviewMeetingLocationType.MicrosoftTeams)}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <ListItemIcon>
                <Avatar src={MicrosoftTeamsLogo} />
              </ListItemIcon>
              <ListItemText>
                <Typography>{t('hiring-dashboard-common:common.location.microsoftTeams')}</Typography>
                <Typography variant="caption" color="text.secondary" noWrap={false}>
                  {t('hiring-dashboard-common:common.location.microsoftTeamsDesc')}
                </Typography>
              </ListItemText>
            </Stack>
          </MenuItem>
        )}

        <MenuItem
          data-testid={`interview-meeting-location-menu-item-${InterviewMeetingLocationType.CustomLink}`}
          value={InterviewMeetingLocationType.CustomLink}
          onClick={() => handleMenuItemClick(InterviewMeetingLocationType.CustomLink)}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <ListItemIcon>
              <LinkIcon />
            </ListItemIcon>
            <ListItemText>
              <Typography>{t('hiring-dashboard-common:common.location.customLink')}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ textWrap: 'wrap' }}>
                {t('hiring-dashboard-common:common.location.customLinkDesc')}
              </Typography>
            </ListItemText>
          </Stack>
        </MenuItem>

        <Divider />

        <ListSubheader>{t('hiring-dashboard-common:common.location.phoneNumber')}</ListSubheader>

        {showCandidatePhone && (
          <MenuItem
            data-testid={`interview-meeting-location-menu-item-${InterviewMeetingLocationType.CandidatePhone}`}
            value={InterviewMeetingLocationType.CandidatePhone}
            onClick={() => handleMenuItemClick(InterviewMeetingLocationType.CandidatePhone)}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <ListItemIcon>
                <PhoneWithArrowPointingOutwardsIcon />
              </ListItemIcon>
              <ListItemText>
                <Typography>{t('hiring-dashboard-common:common.location.candidatePhone')}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ textWrap: 'wrap' }}>
                  {t('hiring-dashboard-common:common.location.candidatePhoneDesc')}
                </Typography>
              </ListItemText>
            </Stack>
          </MenuItem>
        )}

        <MenuItem
          data-testid={`interview-meeting-location-menu-item-${InterviewMeetingLocationType.CustomPhone}`}
          value={InterviewMeetingLocationType.CustomPhone}
          onClick={() => handleMenuItemClick(InterviewMeetingLocationType.CustomPhone)}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <ListItemIcon>
              <PhoneWithArrowPointingInwardsIcon />
            </ListItemIcon>
            <ListItemText>
              <Typography>{t('hiring-dashboard-common:common.location.customPhone')}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ textWrap: 'wrap' }}>
                {t('hiring-dashboard-common:common.location.customPhoneDesc')}
              </Typography>
            </ListItemText>
          </Stack>
        </MenuItem>

        <Divider />

        <ListSubheader>{t('hiring-dashboard-common:common.location.other')}</ListSubheader>

        <MenuItem
          data-testid={`interview-meeting-location-menu-item-${InterviewMeetingLocationType.CustomText}`}
          value={InterviewMeetingLocationType.CustomText}
          onClick={() => handleMenuItemClick(InterviewMeetingLocationType.CustomText)}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <ListItemIcon>
              <TextIcon />
            </ListItemIcon>
            <ListItemText>
              <Typography>{t('hiring-dashboard-common:common.location.customText')}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ textWrap: 'wrap' }}>
                {t('hiring-dashboard-common:common.location.customTextDesc')}
              </Typography>
            </ListItemText>
          </Stack>
        </MenuItem>

        <MenuItem
          data-testid={`interview-meeting-location-menu-item-${InterviewMeetingLocationType.None}`}
          value={InterviewMeetingLocationType.None}
          onClick={() => handleMenuItemClick(InterviewMeetingLocationType.None)}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <ListItemIcon>
              <NoneIcon />
            </ListItemIcon>
            <ListItemText>{t('hiring-dashboard-common:common.none')}</ListItemText>
          </Stack>
        </MenuItem>
      </Menu>
    </>
  );
};

export default InterviewMeetingLocationMenu;
